import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useBranding } from "../lib/branding";
import { toast } from "sonner";

const MX_GRADIENT = { background: "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)" };

export default function ForgotPassword() {
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Form */}
      <div className="flex-1 bg-[#F0F1F7] flex flex-col justify-center px-10 py-12 lg:px-16 max-w-[560px]">
        <div className="mb-10">
          {branding.logo_data ? (
            <img src={branding.logo_data} alt={branding.name} className="h-12 w-auto max-w-[200px] object-contain" />
          ) : (
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-[10px] flex items-center justify-center text-white font-[900] text-lg flex-shrink-0"
                style={MX_GRADIENT}
              >
                M
              </div>
              <div>
                <div className="text-[15px] font-[800] tracking-tight text-[#1A1A2E] uppercase leading-none">
                  {branding.name}
                </div>
                <div className="text-[9px] font-[600] text-[#9CA3AF] tracking-[0.18em] uppercase mt-1">
                  Learning Hub
                </div>
              </div>
            </div>
          )}
        </div>

        <h1 className="text-[34px] font-[900] text-[#1A1A2E] leading-tight mb-2">Reset password.</h1>
        <p className="text-sm text-[#6B7280] mb-8">We'll send a reset link to your registered email.</p>

        {sent ? (
          <div className="space-y-5" data-testid="forgot-password-sent">
            <div className="bg-white rounded-[12px] border border-[#E8EAEF] p-5 text-sm text-[#1A1A2E]">
              If <strong>{email}</strong> is registered, we've sent a password reset link. It expires in 60 minutes.
            </div>
            <Link
              to="/login"
              data-testid="forgot-password-back-link"
              className="flex items-center justify-center w-full text-white rounded-[12px] min-h-[52px] py-3 font-[700] text-sm"
              style={MX_GRADIENT}
            >
              Back to Sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
            <div>
              <label className="block text-[10px] font-[700] text-[#6B7280] mb-2 uppercase tracking-[0.12em]">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@mxacademy.in"
                data-testid="forgot-password-email"
                className="w-full rounded-[10px] border border-[#E8EAEF] px-4 py-3 min-h-[48px] bg-white focus:outline-none focus:border-[#6C47FF] text-sm text-[#1A1A2E] placeholder-[#C4C9DA]"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-password-submit"
              className="w-full text-white rounded-[12px] min-h-[52px] py-3 font-[700] text-sm disabled:opacity-60"
              style={MX_GRADIENT}
            >
              {loading ? "Sending…" : "Send reset link"}
            </button>
            <Link
              to="/login"
              data-testid="forgot-password-cancel"
              className="block text-center text-sm font-[600] text-[#6C47FF] hover:underline"
            >
              Back to Sign in
            </Link>
          </form>
        )}
      </div>

      {/* Right Panel — Hero */}
      <div
        className="hidden lg:flex flex-1 flex-col justify-center px-16 py-12 text-white relative overflow-hidden"
        style={MX_GRADIENT}
      >
        <div className="absolute top-[-80px] right-[-80px] w-[320px] h-[320px] rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[240px] h-[240px] rounded-full bg-white/10 pointer-events-none" />
        <div className="relative z-10">
          <h2 className="text-[48px] font-[900] leading-[1.05] mb-6">
            Secure<br />your account.
          </h2>
          <p className="text-white/80 text-base leading-relaxed max-w-sm">
            Password reset links are valid for 60 minutes and can only be used once.
          </p>
        </div>
      </div>
    </div>
  );
}
